

import { createContext, useContext, useState, useMemo, useEffect, useRef } from "react";
import { Plugin } from "../types/enums/Plugin";
import { EventContext } from "./EventProvider";

type RoleContextType = {
  role: string;
  /**
   * CUSTOM-aware write-gate bridge. For named roles this equals `role`
   * (OWNER/ADMIN/EDITOR/VIEWER); for a CUSTOM role it collapses to the
   * server-computed floor — "EDITOR" (template has any write cap) or "VIEWER"
   * (read-only). Use `legacyRoleFloor !== "VIEWER"` for write-affordance gates
   * so a read-only CUSTOM user isn't treated like an editor. Mirrors
   * `useCapabilities().legacyRoleFloor`.
   */
  legacyRoleFloor: string;
  setRole: (role: string) => void;
  isArchived: boolean;
  setIsArchived: (isArchived: boolean) => void;
  /** Event-plan read-only lock (9-month lifecycle). Like `isArchived`, gates
   *  write affordances — UX mirror of the backend G20 planLocked filter. */
  isPlanLocked: boolean;
  plugins: Plugin[];
  setPlugins: (plugins: Plugin[]) => void;
};

const RoleContext = createContext<RoleContextType | undefined>(undefined);

export const RoleProvider = ({
  children,
  role: roleProps,
  legacyRoleFloor: legacyRoleFloorProps,
  isArchived: isArchivedProps,
  isPlanLocked: isPlanLockedProps,
  plugins: pluginsProps,
}: {
  children: React.ReactNode;
  role?: string;
  /** Server-computed CUSTOM floor. Pass on subtrees without an EventProvider
   *  (e.g. the standalone builder pages). Derived otherwise. */
  legacyRoleFloor?: string;
  isArchived?: boolean;
  isPlanLocked?: boolean;
  plugins?: Plugin[];
}) => {
  // When props are omitted, fall back to EventProvider context (set by the event layout).
  // useContext is always called (no conditional hooks) and returns undefined when
  // RoleProvider is rendered outside an EventProvider.
  const eventCtx = useContext(EventContext);

  // F5 (PR 7): default to "VIEWER" when every source is empty. The
  // previous fallback of "" made `role !== "VIEWER"` evaluate to true,
  // which flipped write affordances ON for a caller whose role
  // couldn't be resolved — precisely the opposite of what we want.
  // VIEWER is the safest fail-closed posture: the caller sees no
  // write buttons, and any backend mutation would be rejected anyway.
  const resolvedRoleRaw = roleProps ?? eventCtx?.eventRole?.role ?? "";
  const resolvedRole = resolvedRoleRaw.length > 0 ? resolvedRoleRaw : "VIEWER";
  // Server floor source for CUSTOM roles (prop on provider-less builder
  // subtrees, else the EventProvider's resolved eventRole). Only consulted
  // when the role is CUSTOM; named roles use the role string directly.
  const legacyFloorSource =
    legacyRoleFloorProps ?? eventCtx?.eventRole?.legacyRoleFloor;
  const resolvedIsArchived = isArchivedProps ?? eventCtx?.event?.archived ?? false;
  const resolvedIsPlanLocked = isPlanLockedProps ?? eventCtx?.event?.planLocked ?? false;
  // Reuse a stable empty array sentinel — `?? []` would produce a fresh
  // reference each render and would make the sync effect below infinite-loop
  // when both sources are undefined.
  const emptyPluginsRef = useRef<Plugin[]>([]);
  const resolvedPlugins =
    pluginsProps ??
    (eventCtx?.event?.plugins as Plugin[] | undefined) ??
    emptyPluginsRef.current;

  const [role, setRole] = useState(resolvedRole);
  const [isArchived, setIsArchived] = useState(resolvedIsArchived);
  const [isPlanLocked, setIsPlanLocked] = useState(resolvedIsPlanLocked);
  const [plugins, setPlugins] = useState(resolvedPlugins);

  // Sync state when the source props/context-fallback change. Primitives
  // (role, isArchived) compare by value so the effect bails when unchanged;
  // plugins is array-identity, stabilized via emptyPluginsRef when neither
  // source defines it.
  useEffect(() => { setRole(resolvedRole); }, [resolvedRole]);
  useEffect(() => { setIsArchived(resolvedIsArchived); }, [resolvedIsArchived]);
  useEffect(() => { setIsPlanLocked(resolvedIsPlanLocked); }, [resolvedIsPlanLocked]);
  useEffect(() => { setPlugins(resolvedPlugins); }, [resolvedPlugins]);

  const contextValue = useMemo(() => {
    // Named roles: floor == role. CUSTOM: server floor (EDITOR|VIEWER),
    // fail-safe to VIEWER (hide write affordances) when unresolved.
    const legacyRoleFloor =
      role === "CUSTOM" ? (legacyFloorSource ?? "VIEWER") : role;
    return {
      role, legacyRoleFloor, setRole, isArchived, setIsArchived,
      isPlanLocked, plugins, setPlugins,
    };
  }, [role, legacyFloorSource, isArchived, isPlanLocked, plugins]);

  return (
    <RoleContext.Provider value={contextValue}>
      {children}
    </RoleContext.Provider>
  );
};

export const useRole = () => {
  const context = useContext(RoleContext);
  if (!context) {
    throw new Error("useRole must be used within a RoleProvider");
  }
  return context;
};

/**
 * The role context when there is one, `null` outside a `RoleProvider`. For
 * components that are mounted both under a provider (the sent-mails page) and
 * without one (a guest drawer in a test), and that only NARROW what they
 * show by role — a missing provider must read as "least privilege", never as
 * a crash. Prefer `useRole` wherever the provider is guaranteed.
 */
export const useOptionalRole = () => useContext(RoleContext) ?? null;
